import React, { useEffect, useCallback, useState, memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Form as FinalForm } from 'react-final-form';
import { Form } from 'antd';

import Modal from 'components/Shared/Modals';
import injectValidators from 'components/Shared/Forms/injectValidators';
import EmailInput from 'components/Shared/Forms/EmailInput';
import Button from 'components/Shared/Common/Button';
import { requestPatchUserEmail } from 'actions/session';

const validate = ({ email = {} } = {}) => {
    const errors = {}
    if (email.newEmail !== email.confirmEmail) {
        errors.email = errors.email || {};
        errors.email.confirmEmail = 'Emails are differents';
    }
    return errors;
}

const formatMessages = intl => ({
    title: intl.formatMessage({ id: 'modal.requestedActions.emailReset.title' }),
    remindMeLater: intl.formatMessage({ id: 'global.button.remindMeLater' }),
    save: intl.formatMessage({ id: 'global.button.save' }),
    content: intl.formatMessage({ id: 'modal.requestedActions.emailReset.content' }),
});

const Footer = ({ translations, handleClose, handleSubmit, pristine, submitting, valid }) => (
    <>
        <Button
            key="skip"
            color="secondary"
            onClick={handleClose}
            isDisabled={submitting}
            isShadow
        >
            {translations.remindMeLater}
        </Button>
        <Button
            key="save"
            color="primary"
            onClick={handleSubmit}
            isShadow
            isDisabled={pristine || submitting || !valid}
        >
            {translations.save}
        </Button>
    </>
);

const RequestedChangeEmail = ({ 
    intl, 
    requestedActions = [],
    validators,
    error,
    onSubmit
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const translations = useMemo(() => formatMessages(intl), [intl]);
    const hasEmailWarning = requestedActions.some(x => x.key === 'emailReset');

    const submit = useCallback((values, form) => {
        setIsOpen(false);
        onSubmit(values, form);
    }, [onSubmit]);

    const handleClose = useCallback(() => setIsOpen(false), []);

    useEffect(() => {
        if (hasEmailWarning) {
            setIsOpen(true);
        }
    }, [hasEmailWarning]);

    return (
        <FinalForm onSubmit={submit} validate={validate}>
            {({ handleSubmit, ...rest }) => (
                <Modal
                    isOpen={isOpen}
                    title={translations.title}
                    onClose={handleClose}
                    footer={(
                        <Footer
                            translations={translations}
                            handleClose={handleClose}
                            handleSubmit={handleSubmit}
                            {...rest}
                        />
                    )}
                >
                    {translations.content}
                    <br />
                    <br />
                    <Form onSubmit={handleSubmit} layout="vertical">
                        <EmailInput name="email" validators={validators} />

                        {error && intl.formatMessage({
                            id: error,
                            defaultMessage: 'Error'
                        })}
                    </Form>
                </Modal>
            )}
        </FinalForm>
    );
}

RequestedChangeEmail.propTypes = {
    intl: PropTypes.any.isRequired,
    requestedActions: PropTypes.array,
};

const mapDispatchToProps = (dispatch) => ({
    onSubmit: ({ email } = {}, form) => {
        if (form.getState().dirty) {
            dispatch(requestPatchUserEmail(email?.newEmail));
        }
    },
});

export default compose(
    injectValidators,
    connect(null, mapDispatchToProps),
    injectIntl,
    memo
)(RequestedChangeEmail);
